import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout/Layout';
import { toast } from 'react-toastify';
import Seo from '../../components/Seo';
import logo from '../../images/logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const statusColors = { Open: '#3498db', 'In Progress': '#f39c12', Resolved: '#2ed573', Closed: '#636e72' };

const TicketDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [attachPreview, setAttachPreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const commentsEndRef = useRef(null);

    const fetchTicket = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}tickets.php?ticket_id=${id}`);
            const data = await res.json();
            if (data.error) { toast.error(data.error); navigate('/support'); return; }
            setTicket(data);
        } catch (err) {
            toast.error(t('support.failedLoad') || 'Failed to load ticket');
        }
        setLoading(false);
    }, [id, t, navigate]);

    useEffect(() => {
        if (user) fetchTicket();
    }, [user, id, fetchTicket]);

    useEffect(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [ticket?.comments]);

    const handleAttachChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setAttachment(file);
        setAttachPreview(URL.createObjectURL(file));
    };

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!comment.trim() && !attachment) return;
        setIsSubmitting(true);

        let attachmentUrl = null;

        // Upload attachment if present
        if (attachment) {
            const formData = new FormData();
            formData.append('image', attachment);
            try {
                const uploadRes = await fetch(`${API_URL}upload.php`, { method: 'POST', body: formData });
                const uploadResult = await uploadRes.json();
                if (uploadResult.success) {
                    attachmentUrl = uploadResult.url;
                } else {
                    toast.error(t('support.uploadFailed') || 'Attachment upload failed');
                    setIsSubmitting(false);
                    return;
                }
            } catch (err) {
                toast.error(t('support.uploadError') || 'Attachment upload error');
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const res = await fetch(`${API_URL}tickets.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_comment',
                    ticket_id: id,
                    user_id: user.id,
                    message: comment,
                    attachment_url: attachmentUrl
                })
            });
            const result = await res.json();
            if (result.success) {
                setComment('');
                setAttachment(null);
                setAttachPreview(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                await fetchTicket();
            } else {
                toast.error(result.error);
            }
        } catch (err) {
            toast.error(t('support.failedComment') || 'Failed to post comment');
        }
        setIsSubmitting(false);
    };

    const getAvatarSrc = (pic) => {
        if (!pic) return logo;
        return pic.startsWith('http') ? pic : API_URL + pic;
    };

    if (authLoading || !user) return null;

    return (
        <Layout hideScrollbar>
            <Seo title={ticket ? `${t('support.ticketId')} #${ticket.id}` : ''} />
            <main className="page_content pt-150 pb-120">
                <div className="container" style={{ maxWidth: '860px' }}>
                    <Link to="/support" className="back-link mb-4 d-inline-flex align-items-center gap-2">
                        <i className="fas fa-arrow-left"></i> {t('support.backToTickets')}
                    </Link>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status"></div>
                        </div>
                    ) : ticket ? (
                        <>
                            {/* Ticket Header */}
                            <div className="ticket-detail-card p-4 mb-4">
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                                    <div>
                                        <span className="badge bg-secondary mb-2">{ticket.category_name}</span>
                                        <h2 className="text-white mb-0">#{ticket.id} — {ticket.subject}</h2>
                                        <small className="text-muted">{t('support.openedBy')} {new Date(ticket.created_at).toLocaleString()} {t('common.by') || 'by'} {ticket.username}</small>
                                    </div>
                                    <span className="ticket-status-badge" style={{ background: statusColors[ticket.status] + '22', color: statusColors[ticket.status], border: `1px solid ${statusColors[ticket.status]}44` }}>
                                        {ticket.status}
                                    </span>
                                </div>

                                {/* Original message */}
                                <div className="comment-bubble original p-3 mb-0">
                                    <p className="text-white mb-0" style={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</p>
                                    {ticket.attachment_url && (
                                        <div className="mt-3">
                                            <a href={ticket.attachment_url.startsWith('http') ? ticket.attachment_url : API_URL + ticket.attachment_url} target="_blank" rel="noreferrer">
                                                <img
                                                    src={ticket.attachment_url.startsWith('http') ? ticket.attachment_url : API_URL + ticket.attachment_url}
                                                    alt="Attachment"
                                                    className="ticket-attachment-img"
                                                />
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Comments Thread */}
                            {ticket.comments && ticket.comments.length > 0 && (
                                <div className="comments-thread mb-4">
                                    <h5 className="text-white mb-3"><i className="fas fa-comments me-2"></i>{t('support.replies')} ({ticket.comments.length})</h5>
                                    {ticket.comments.map(c => {
                                        const isMe = c.user_id === user.id;
                                        const isAdmin = c.role === 'super_admin';
                                        return (
                                            <div key={c.id} className={`comment-row ${isMe ? 'comment-mine' : ''} mb-3`}>
                                                <img
                                                    src={getAvatarSrc(c.profile_picture)}
                                                    alt=""
                                                    className="comment-avatar"
                                                    style={{ backgroundColor: '#fff', padding: c.profile_picture ? '0' : '2px' }}
                                                />
                                                <div className={`comment-bubble p-3 ${isAdmin ? 'admin-bubble' : ''}`}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="fw-bold text-white">
                                                            {c.username}
                                                            {isAdmin && <span className="badge bg-primary ms-2 small">{t('support.supportStaff') || 'Support'}</span>}
                                                        </span>
                                                        <small className="text-muted">{new Date(c.created_at).toLocaleString()}</small>
                                                    </div>
                                                    <p className="text-white-70 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{c.message}</p>
                                                    {c.attachment_url && (
                                                        <div className="mt-2">
                                                            <a href={c.attachment_url.startsWith('http') ? c.attachment_url : API_URL + c.attachment_url} target="_blank" rel="noreferrer">
                                                                <img
                                                                    src={c.attachment_url.startsWith('http') ? c.attachment_url : API_URL + c.attachment_url}
                                                                    alt="Attachment"
                                                                    className="ticket-attachment-img"
                                                                />
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={commentsEndRef}></div>
                                </div>
                            )}

                            {/* Reply Form */}
                            {ticket.status !== 'Closed' ? (
                                <div className="ticket-detail-card p-4">
                                    <h5 className="text-white mb-3"><i className="fas fa-reply me-2"></i>{t('support.addReply')}</h5>
                                    <form onSubmit={handleSubmitComment}>
                                        <textarea
                                            className="form-control-modern mb-3"
                                            rows="4"
                                            placeholder={t('support.writeReply') || "Write your reply here..."}
                                            value={comment}
                                            onChange={e => setComment(e.target.value)}
                                        />

                                        {/* Attachment preview */}
                                        {attachPreview && (
                                            <div className="attach-preview mb-3">
                                                <img src={attachPreview} alt="Preview" className="ticket-attachment-img" />
                                                <button type="button" className="btn-remove-attach" onClick={() => { setAttachment(null); setAttachPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        )}

                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                            <label className="btn-attach" title="Attach screenshot">
                                                <i className="fas fa-image me-2"></i>
                                                {attachment ? attachment.name : t('support.attachScreenshot')}
                                                <input type="file" accept="image/*" onChange={handleAttachChange} ref={fileInputRef} style={{ display: 'none' }} />
                                            </label>
                                            <button type="submit" className="btn-save" disabled={isSubmitting || (!comment.trim() && !attachment)}>
                                                {isSubmitting ? t('support.sending') : t('support.sendReply')}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <div className="ticket-detail-card p-4 text-center">
                                    <i className="fas fa-lock text-muted fa-2x mb-2"></i>
                                    <p className="text-muted mb-0">{t('support.ticketClosed')}</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-white">{t('support.ticketNotFound')}</p>
                    )}
                </div>
            </main>
            <style>{`
                .back-link { color: rgba(255,255,255,0.5); text-decoration: none; font-size: 14px; transition: 0.2s; }
                .back-link:hover { color: #6c5ce7; }
                .ticket-detail-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 16px; }
                .ticket-status-badge { padding: 6px 14px; border-radius: 30px; font-size: 13px; font-weight: 600; }
                .comment-row { display: flex; gap: 12px; align-items: flex-start; }
                .comment-row.comment-mine { flex-direction: row-reverse; }
                .comment-avatar { width: 38px; height: 38px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
                .comment-bubble { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; flex: 1; }
                .comment-bubble.original { background: rgba(108,92,231,0.08); border-color: rgba(108,92,231,0.2); }
                .comment-bubble.admin-bubble { background: rgba(52,152,219,0.07); border-color: rgba(52,152,219,0.2); }
                .comment-row.comment-mine .comment-bubble { background: rgba(108,92,231,0.1); border-color: rgba(108,92,231,0.3); }
                .text-white-70 { color: rgba(255,255,255,0.75); }
                .ticket-attachment-img { max-width: 320px; max-height: 220px; border-radius: 8px; object-fit: cover; border: 1px solid rgba(255,255,255,0.1); cursor: pointer; transition: 0.2s; }
                .ticket-attachment-img:hover { transform: scale(1.02); }
                .form-control-modern { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #fff; width: 100%; padding: 12px 15px; border-radius: 8px; resize: vertical; }
                .form-control-modern:focus { border-color: #6c5ce7; outline: none; }
                .btn-attach { background: rgba(255,255,255,0.05); border: 1px dashed rgba(255,255,255,0.2); color: rgba(255,255,255,0.6); padding: 9px 18px; border-radius: 8px; cursor: pointer; font-size: 13px; transition: 0.2s; }
                .btn-attach:hover { border-color: #6c5ce7; color: #6c5ce7; }
                .btn-save { background: #6c5ce7; color: #fff; border: none; padding: 10px 28px; border-radius: 30px; font-weight: 600; cursor: pointer; transition: 0.3s; }
                .btn-save:hover:not(:disabled) { background: #5a4ad1; transform: translateY(-1px); }
                .btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
                .attach-preview { position: relative; display: inline-block; }
                .btn-remove-attach { position: absolute; top: -8px; right: -8px; width: 24px; height: 24px; border-radius: 50%; background: #ff4757; color: #fff; border: none; cursor: pointer; font-size: 11px; display: flex; align-items: center; justify-content: center; }
            `}</style>
        </Layout>
    );
};

export default TicketDetail;
